"use client";

import aiParallelDiallerImage from "@/../public/images/showcase/ai-parallel-dialler.png";
import aiPersonalizerImage from "@/../public/images/showcase/ai-personalizer.png";
import buyingIntentImage from "@/../public/images/showcase/buying-intent.png";
import contactDataImage from "@/../public/images/showcase/contact-data.png";
import customResearchImage from "@/../public/images/showcase/custom-research.png";
import emailSequencerImage from "@/../public/images/showcase/email-sequencer.png";
import emailWarmupImage from "@/../public/images/showcase/email-warmup.png";
import keyCompanyInsightsImage from "@/../public/images/showcase/key-company-insights.png";
import linkedinDataImage from "@/../public/images/showcase/linkedin-data.png";
import prospectListImage from "@/../public/images/showcase/prospect-list.png";
import realTimeBuyingTriggersImage from "@/../public/images/showcase/real-time-buying-triggers.png";
import technologyStackFinancialsImage from "@/../public/images/showcase/technology-stack-&-financials.png";
import { cn } from "@/lib/utils";
import Image, { type StaticImageData } from "next/image";
import { useState, type PropsWithChildren } from "react";

const ShowcaseGroup = ({
  title,
  className,
  children,
}: PropsWithChildren<{ title: string; className: string }>) => {
  return (
    <div className={"flex flex-col gap-sm " + className}>
      <h2 className="text-xs uppercase font-bold pl-lg">{title}</h2>
      {children}
    </div>
  );
};

const ShowcaseItem = ({
  icon,
  text,
  selectedItem,
  onSelect,
}: {
  icon: string;
  text?: string;
  selectedItem: string;
  onSelect: (text: string) => void;
}) => {
  return (
    <button
      className={cn(
        "flex gap-xl items-center rounded-full px-base md:px-lg py-sm md:py-base transition-all bg-slate-300 bg-opacity-0 hover:bg-opacity-20 active:bg-opacity-50",
        selectedItem === text ? "bg-opacity-40 hover:bg-opacity-50" : ""
      )}
      onClick={() => onSelect(text!)}
    >
      <Image src={icon} alt="icon" width={24} height={24} />
      <div className="text-white text-xs md:text-sm lg:text-base">{text}</div>
    </button>
  );
};

const Sidebar = ({
  selectedItem,
  setSelectedItem,
}: {
  selectedItem: string;
  setSelectedItem: (item: string) => void;
}) => {
  return (
    <div className="shrink-0 flex flex-col gap-3xl py-2xl md:py-4xl lg:py-5xl pl-xl md:pl-3xl lg:pl-4xl">
      <ShowcaseGroup title="Identify" className="text-sky-500">
        {[
          ["/images/showcase/user-check-alt.svg", "Prospect List"],
          ["/images/showcase/monitor-sun.svg", "Real-Time Buying Triggers"],
          ["/images/showcase/bags-shopping.svg", "Buying Intent Data"],
        ].map(([icon, text], i) => (
          <ShowcaseItem
            {...{ icon, text, selectedItem }}
            key={i}
            onSelect={setSelectedItem}
          />
        ))}
      </ShowcaseGroup>
      <ShowcaseGroup title="Enrich" className="text-yellow-500">
        {[
          ["/images/showcase/square-phone.svg", "Contact Data"],
          ["/images/showcase/chart-network.svg", "LinkedIn Data"],
          ["/images/showcase/lightbulb-alt.svg", "Key Company Insights"],
          ["/images/showcase/sitemap.svg", "Technology Stack & Financials"],
          ["/images/showcase/message-square-search.svg", "Custom Research"],
        ].map(([icon, text], i) => (
          <ShowcaseItem
            {...{ icon, text, selectedItem }}
            key={i}
            onSelect={setSelectedItem}
          />
        ))}
      </ShowcaseGroup>
      <ShowcaseGroup title="Engage" className="text-green-300">
        {[
          ["/images/showcase/lightbulb-exclamation-alt.svg", "AI Personalizer"],
          ["/images/showcase/mail-star.svg", "Email Sequencer"],
          ["/images/showcase/mail-open.svg", "Email Warmup"],
          [
            "/images/showcase/phone-call.svg",
            "AI Parallel Dialler (Coming Soon!)",
          ],
        ].map(([icon, text], i) => (
          <ShowcaseItem
            {...{ icon, text, selectedItem }}
            key={i}
            onSelect={setSelectedItem}
          />
        ))}
      </ShowcaseGroup>
    </div>
  );
};

const Window = ({ selectedItem }: { selectedItem: string }) => {
  const map: Record<string, StaticImageData> = {
    "Prospect List": prospectListImage,
    "Real-Time Buying Triggers": realTimeBuyingTriggersImage,
    "Buying Intent Data": buyingIntentImage,
    "Contact Data": contactDataImage,
    "LinkedIn Data": linkedinDataImage,
    "Key Company Insights": keyCompanyInsightsImage,
    "Technology Stack & Financials": technologyStackFinancialsImage,
    "Custom Research": customResearchImage,
    "AI Personalizer": aiPersonalizerImage,
    "Email Sequencer": emailSequencerImage,
    "Email Warmup": emailWarmupImage,
    "AI Parallel Dialler (Coming Soon!)": aiParallelDiallerImage,
  };

  return (
    <div className="flex flex-col justify-center gap-3xl py-5xl">
      <Image
        src={map[selectedItem]}
        alt={selectedItem}
        className="rounded-l-md overflow-hidden "
      />
    </div>
  );
};

const Showcase = () => {
  const [selectedItem, setSelectedItem] = useState<string>("Prospect List");

  return (
    <div className="rounded-2xl bg-[#2B2A34] flex items-stretch gap-5xl overflow-clip">
      <Sidebar {...{ selectedItem, setSelectedItem }} />
      <Window {...{ selectedItem }} />
    </div>
  );
};

export default () => {
  return (
    <>
      <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-center mb-3xl">
        One place to start kicking ass in outbound
      </h1>
      <Showcase />
    </>
  );
};
